import { MOD_LINK, routes, SOCIALS, SUPPORT_EMAIL } from '@constants';
import { withFlexProps } from '@hocs';
import {
  useGetDownloadedAvatar,
  useNavigateWithOfflineMode,
  useScreenSize,
} from '@hooks';
import { userApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { ReactComponent as Arrow } from '@svgs/arrow.svg';
import { ReactComponent as FacebookIcon } from '@svgs/facebook-green.svg';
import { ReactComponent as InfoIcon } from '@svgs/info.svg';
import { ReactComponent as InstagramIcon } from '@svgs/instagram-green.svg';
import { ReactComponent as LogoEN } from '@svgs/logo-outlined-en.svg';
import { ReactComponent as LogoUA } from '@svgs/logo-outlined-ua.svg';
import { ReactComponent as MoD } from '@svgs/mod.svg';
import { ReactComponent as PasskeyIcon } from '@svgs/passkey.svg';
import { ReactComponent as TiktokIcon } from '@svgs/tiktok-green.svg';
import { ReactComponent as YoutubeIcon } from '@svgs/youtube-green.svg';
import { func, object } from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useOnlineStatusDetector } from '../../hooks/useOnlineStatusDetector';
import { Icon } from '../Icon';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { footerLayoutByVariant } from './Footer.layoutProps';

const Footer = ({ innerContainerProps, logInButtonClick }) => {
  const { jwtAuthenticationToken, language } = useSelector(
    (state) => state.authSlice
  );

  const { isOnline } = useOnlineStatusDetector();
  const downloadedAvatar = useGetDownloadedAvatar();

  const { data } = userApi.useGetUserQuery(undefined, {
    skip: !jwtAuthenticationToken || !isOnline,
  });

  const { device, dynamicWidth, isTabletLG } = useScreenSize();
  const isLess1280 = useMemo(() => dynamicWidth < 1280, [dynamicWidth]);
  const layout = footerLayoutByVariant[device];
  const { i18n } = useTranslation();

  const Logo = {
    en: LogoEN,
    ua: LogoUA,
  };

  const handleNavigateInstagram = () => {
    window.open(SOCIALS.INSTAGRAM, '_blank');
  };
  const handleNavigateYoutube = () => {
    window.open(SOCIALS.YOUTUBE, '_blank');
  };
  const handleNavigateFacebook = () => {
    window.open(SOCIALS.FACEBOOK, '_blank');
  };
  const handleNavigateTiktok = () => {
    window.open(SOCIALS.TIKTOK, '_blank');
  };

  const { navigate } = useNavigateWithOfflineMode();
  return (
    <Flex flexDirection="column" width="100%">
      <Flex
        bg={theme.colors.background}
        borderBottomColor={theme.colors.tundora}
        borderBottomStyle="solid"
        borderBottomWidth={1}
        justifyContent="space-between"
        width="100%"
        {...(isTabletLG && { flexDirection: 'column' })}
        {...layout}
        {...innerContainerProps}
      >
        <Flex>
          <Icon
            height={isTabletLG ? 152 : 183}
            SVG={Logo[language || 'en']}
            width={isTabletLG ? 114 : 137}
          />
          <Flex
            flexDirection="column"
            flexGrow={0}
            gap={8}
            justifyContent="center"
            ml={28}
            mr={28}
          >
            <Typography
              color={theme.colors.white[60]}
              maxWidth={255}
              variant="MS-regular-s16-lh25"
            >
              {i18n.t('Footer.PlatformDescription')}
            </Typography>
            <Typography
              color={theme.colors.schoolBusYellow[100]}
              variant="MS-regular-s16-lh25"
            >
              <a
                href={SOCIALS.LANDING}
                rel="noreferrer"
                style={{
                  color: theme.colors.schoolBusYellow[100],
                  textDecoration: 'none',
                }}
                target="_blank"
              >
                {i18n.t('Footer.WebsiteLink')}
              </a>
            </Typography>
          </Flex>
        </Flex>
        <Flex
          alignItems="center"
          gap={layout?.linksGap}
          {...(isTabletLG && {
            flexDirection: 'column',
            p: 16,
          })}
        >
          <Button
            as={Typography}
            color={theme.colors.white[100]}
            cursor="pointer"
            fontSize={isTabletLG ? 18 : 16}
            height={27}
            onClick={() => navigate(routes.courses)}
            textAlign="center"
            variant="textButton"
          >
            {i18n.t('Footer.Courses')}
          </Button>
          <Button
            as={Typography}
            color={theme.colors.white[100]}
            cursor="pointer"
            fontSize={isTabletLG ? 18 : 16}
            height={27}
            onClick={
              jwtAuthenticationToken
                ? () => navigate(routes.learningMap)
                : logInButtonClick
            }
            textAlign="center"
            variant="textButton"
          >
            {i18n.t('Footer.LearningMap')}
          </Button>
          <Button
            as={Typography}
            color={theme.colors.white[100]}
            cursor="pointer"
            fontSize={isTabletLG ? 18 : 16}
            height={27}
            onClick={() => navigate(routes.activities)}
            textAlign="center"
            to={routes.activities}
            variant="textButton"
          >
            {i18n.t('Footer.Activities')}
          </Button>
          <Button
            as={Typography}
            color={theme.colors.white[100]}
            cursor="pointer"
            fontSize={isTabletLG ? 18 : 16}
            height={27}
            onClick={() => navigate(routes.about)}
            textAlign="center"
            to={routes.about}
            variant="textButton"
          >
            {i18n.t('Footer.AboutUs')}
          </Button>
          {isTabletLG && <LanguageSwitcher />}
          {jwtAuthenticationToken ? (
            <Button
              onClick={() => navigate(routes.profile)}
              variant="textButton"
            >
              {(isOnline ? data?.data.avatar : downloadedAvatar) && (
                <Image
                  alt="Profile picture"
                  borderRadius={28}
                  height={isTabletLG ? 44 : 56}
                  minHeight={isTabletLG ? 44 : 56}
                  minWidth={isTabletLG ? 44 : 56}
                  src={isOnline ? data?.data.avatar : downloadedAvatar}
                  width={isTabletLG ? 44 : 56}
                />
              )}
            </Button>
          ) : (
            <Button
              border={0}
              onClick={logInButtonClick}
              variant="quaternary"
              whiteSpace="nowrap"
              {...(isTabletLG && { height: 'auto' })}
            >
              <Icon
                color={theme.colors.schoolBusYellow[100]}
                mr={1}
                SVG={PasskeyIcon}
              />
              <Typography
                color={theme.colors.schoolBusYellow[100]}
                variant={
                  isTabletLG ? 'MS-semibold-s18-lh19' : 'MS-semibold-s16-lh17'
                }
              >
                {i18n.t('Footer.Login')}
              </Typography>
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex
        bg={theme.colors.background}
        height={isTabletLG ? 'auto' : 126}
        justifyContent="space-between"
        width="100%"
        {...(isTabletLG && { flexDirection: 'column', gap: 24, pb: 24 })}
        {...innerContainerProps}
      >
        <Flex
          alignItems="center"
          bg={isTabletLG && theme.colors.backgroundLight}
          gap={29}
          height="100%"
          pl={layout?.pl}
          pr={isTabletLG ? layout?.pr : 95}
          {...(isTabletLG && { py: 16 })}
        >
          <Icon SVG={InfoIcon} />
          <Typography
            color={theme.colors.white[60]}
            maxWidth={422}
            variant="MS-regular-s16-lh25"
          >
            {i18n.t('Footer.CallToJoin')}
          </Typography>
        </Flex>
        <Flex
          alignItems="center"
          height="100%"
          pr={layout?.pr}
          {...(isTabletLG && { flexDirection: 'column', pl: layout?.pl })}
        >
          <Flex gap={8}>
            <Button
              borderRadius={29}
              height={layout?.socialButtonSize}
              onClick={handleNavigateInstagram}
              p={0}
              variant="primary"
              width={layout?.socialButtonSize}
            >
              <Icon height={30} SVG={InstagramIcon} width={30} />
            </Button>
            <Button
              borderRadius={29}
              height={layout?.socialButtonSize}
              onClick={handleNavigateYoutube}
              p={0}
              variant="primary"
              width={layout?.socialButtonSize}
            >
              <Icon height={30} SVG={YoutubeIcon} width={30} />
            </Button>
            <Button
              borderRadius={29}
              height={layout?.socialButtonSize}
              onClick={handleNavigateFacebook}
              p={0}
              variant="primary"
              width={layout?.socialButtonSize}
            >
              <Icon height={30} SVG={FacebookIcon} width={30} />
            </Button>
            <Button
              borderRadius={29}
              height={layout?.socialButtonSize}
              onClick={handleNavigateTiktok}
              p={0}
              variant="primary"
              width={layout?.socialButtonSize}
            >
              <Icon height={30} SVG={TiktokIcon} width={30} />
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        as="a"
        bg={theme.colors.schoolBusYellow[100]}
        href={MOD_LINK}
        justifyContent="space-between"
        px={layout?.pl}
        py={16}
        style={{
          textDecoration: 'none',
        }}
        target="_blank"
      >
        <Flex>
          <Icon height={30} mr={15} my="3px" SVG={MoD} width={20} />
          <Typography
            alignSelf="center"
            color={theme.colors.black[100]}
            variant={isTabletLG ? 'MS-regular-s14-lh17' : 'MS-regular-s20-lh24'}
          >
            {i18n.t('Footer.MOD')}
          </Typography>
        </Flex>
        <Icon height={40} mr={15} SVG={Arrow} width={40} />
      </Flex>
      <Flex
        bg={theme.colors.background}
        pb={16}
        pl={layout?.pl}
        pt={16}
        {...(isLess1280 && {
          justifyContent: 'space-between',
          pr: layout?.pr,
        })}
        {...(isTabletLG && {
          flexDirection: 'column-reverse',
          gap: 24,
          pb: 40,
          pr: layout?.pr,
        })}
        width="100%"
        {...innerContainerProps}
      >
        <Flex justifyContent="center">
          <Typography
            color={theme.colors.white[60]}
            mr={18}
            variant="MS-regular-s14-lh22"
          >
            {i18n.t('Footer.WebsiteLink')}
          </Typography>
          <Typography
            color={theme.colors.white[60]}
            mr={isLess1280 ? 0 : 128}
            variant="MS-regular-s14-lh22"
          >
            {i18n.t('Footer.Copyright')}
          </Typography>
        </Flex>
        {(!isLess1280 || isTabletLG) && (
          <Flex justifyContent="center">
            <Button
              color={theme.colors.white[40]}
              fontSize={14}
              fontWeight={500}
              height="fit-content"
              onClick={() => navigate(routes.privacyPolicy)}
              variant="textButton"
            >
              {i18n.t('Footer.PrivacyPolicy')}
            </Button>
          </Flex>
        )}
        <Flex justifyContent="center" ml={!isLess1280 && '128px'}>
          <Button
            as="a"
            color={theme.colors.white[40]}
            fontSize={14}
            fontWeight={500}
            height="fit-content"
            href={`mailto:${SUPPORT_EMAIL}`}
            style={{
              fontSize: '14px',
              textDecoration: 'none',
            }}
            variant="textButton"
          >
            {SUPPORT_EMAIL}
          </Button>
        </Flex>
        {isLess1280 && !isTabletLG && (
          <Flex justifyContent="center">
            <Button
              color={theme.colors.white[40]}
              fontSize={14}
              fontWeight={500}
              height="fit-content"
              onClick={() => navigate(routes.privacyPolicy)}
              variant="textButton"
            >
              {i18n.t('Footer.PrivacyPolicy')}
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

Footer.propTypes = {
  innerContainerProps: object,
  logInButtonClick: func,
};

Footer.defaultProps = {
  innerContainerProps: {},
  logInButtonClick: () => {},
};

export default withFlexProps(Footer);
